import { t } from "@lingui/macro";
import {
  ArrowCounterClockwise,
  Broom,
  Columns,
  DotsThree,
  Eye,
  EyeSlash,
  LinkSimple,
  Palette,
  PencilSimple,
  Plus,
  TrashSimple,
} from "@phosphor-icons/react";
import { useBreakpoint } from "@stylingcv/hooks";
import type { SectionKey, SectionWithItem } from "@stylingcv/schema";
import { defaultSection, defaultSections } from "@stylingcv/schema";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@stylingcv/ui";
import get from "lodash.get";
import { useEffect, useMemo, useRef, useState } from "react";

import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

import { useDeleteSectionStore } from "../../dialogs/delete-section";
import { IconPickerModal } from "./icon-picker-modal";

type Props = { id: SectionKey };

export const SectionOptions = ({ id }: Props) => {
  const { open } = useDialog(id);
  const openDeleteDialog = useDeleteSectionStore((state) => state.open);

  const setValue = useResumeStore((state) => state.setValue);
  const { isMobile } = useBreakpoint();
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [subOpen, setSubOpen] = useState<"rename" | "columns" | null>(null);
  const [iconModalOpen, setIconModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const originalName = get(defaultSections, `${id}.name`, "") as SectionWithItem;
  const section = useResumeStore((state) => {
    try {
      return get(state.resume.data.sections, id) ?? defaultSection;
    } catch {
      return defaultSection;
    }
  }) as SectionWithItem;

  const hasItems = useMemo(() => "items" in section, [section]);
  const isCustomSection = useMemo(() => id.startsWith("custom"), [id]);

  useEffect(() => {
    if (!desktopOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDesktopOpen(false);
        setSubOpen(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [desktopOpen]);

  const closeDropdown = () => {
    setDesktopOpen(false);
    setSubOpen(null);
  };

  const onCreate = () => {
    open("create", { id });
  };

  const toggleSeperateLinks = (checked: boolean) => {
    try {
      setValue(`sections.${id}.separateLinks`, checked);
    } catch {
      // Silent fail
    }
  };

  const toggleVisibility = () => {
    try {
      setValue(`sections.${id}.visible`, !section.visible);
    } catch {
      // Silent fail
    }
  };

  const onResetName = () => {
    try {
      setValue(`sections.${id}.name`, originalName);
    } catch {
      // Silent fail
    }
  };

  const onChangeColumns = (value: string) => {
    try {
      setValue(`sections.${id}.columns`, Number(value));
    } catch {
      // Silent fail
    }
  };

  const onResetItems = () => {
    try {
      setValue(`sections.${id}.items`, []);
    } catch {
      // Silent fail
    }
  };

  const onRemove = () => {
    openDeleteDialog(id);
  };

  const actionText = isCustomSection ? t`Delete Section` : t`Hide Section`;
  const actionIcon = isCustomSection ? <TrashSimple size={16} /> : <EyeSlash size={16} />;

  const closeMobileSheet = () => {
    setMobileSheetOpen(false);
    setSubOpen(null);
  };

  // Mobile — bottom sheet mirroring the desktop dropdown menu style
  if (isMobile) {
    return (
      <>
        <Sheet open={mobileSheetOpen} onOpenChange={(v) => { setMobileSheetOpen(v); if (!v) setSubOpen(null); }}>
          <SheetTrigger asChild>
            <button
              aria-label={t`Section settings`}
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                border: "1px solid var(--border, #e5e5e5)",
                background: "#fff",
                color: "var(--fg-2, #404040)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <DotsThree size={18} weight="bold" />
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-2xl border-t-0 p-0">
            <SheetHeader className="px-4 pb-1 pt-4 text-left rtl:text-right">
              <SheetTitle
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--fg-3, #737373)",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                {section.name}
              </SheetTitle>
            </SheetHeader>

            <div style={{ padding: "4px 8px 24px" }}>
              {/* Group 1: Add + Separate Links */}
              {hasItems && (
                <>
                  <MenuRow
                    icon={<Plus size={16} />}
                    label={t`Add a new item`}
                    shortcut="⌘N"
                    primaryIcon
                    onClick={() => {
                      onCreate();
                      closeMobileSheet();
                    }}
                  />
                  <MenuRow
                    icon={<LinkSimple size={16} />}
                    label={t`Separate Links`}
                    checked={Boolean(section.separateLinks)}
                    onCheckedChange={(v) => { toggleSeperateLinks(v); }}
                  />
                  <div style={{ height: 1, background: "var(--border, #e5e5e5)", margin: "4px 6px" }} />
                </>
              )}

              {/* Hide / Show */}
              <MenuRow
                icon={section.visible ? <Eye size={16} /> : <EyeSlash size={16} />}
                label={section.visible ? t`Hide` : t`Show`}
                shortcut="⌘H"
                onClick={() => {
                  toggleVisibility();
                  closeMobileSheet();
                }}
              />

              {/* Rename (accordion) */}
              <MenuRow
                icon={<PencilSimple size={16} />}
                label={t`Rename`}
                hasSub
                subOpen={subOpen === "rename"}
                onClick={() => setSubOpen(subOpen === "rename" ? null : "rename")}
              />
              {subOpen === "rename" && (
                <div style={{ padding: "4px 10px 8px" }}>
                  <div style={{ display: "flex", gap: 4 }}>
                    <input
                      autoFocus
                      value={section.name}
                      onChange={(e) => setValue(`sections.${id}.name`, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") closeMobileSheet(); }}
                      style={{
                        flex: 1,
                        height: 36,
                        border: "1px solid var(--border, #e5e5e5)",
                        borderRadius: 6,
                        padding: "0 8px",
                        fontSize: 13,
                        color: "var(--fg-1, #171717)",
                        background: "#fff",
                        outline: "none",
                      }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = "var(--accent, #268599)"; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border, #e5e5e5)"; }}
                    />
                    <button
                      onClick={onResetName}
                      title={t`Reset name`}
                      style={{
                        width: 36, height: 36,
                        border: "1px solid var(--border, #e5e5e5)",
                        borderRadius: 6, background: "#fff", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "var(--fg-3, #737373)",
                      }}
                    >
                      <ArrowCounterClockwise size={14} />
                    </button>
                  </div>
                </div>
              )}

              {/* Change Icon */}
              <MenuRow
                icon={<Palette size={16} />}
                label={t`Change Icon`}
                onClick={() => {
                  closeMobileSheet();
                  setTimeout(() => setIconModalOpen(true), 300);
                }}
              />

              {/* Columns (accordion) */}
              <MenuRow
                icon={<Columns size={16} />}
                label={t`Columns`}
                hasSub
                subOpen={subOpen === "columns"}
                onClick={() => setSubOpen(subOpen === "columns" ? null : "columns")}
              />
              {subOpen === "columns" && (
                <div style={{ padding: "4px 10px 8px" }}>
                  <div style={{ display: "flex", gap: 4 }}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        onClick={() => { onChangeColumns(String(n)); closeMobileSheet(); }}
                        style={{
                          flex: 1, height: 32, borderRadius: 6,
                          border: `1px solid ${section.columns === n ? "var(--accent, #268599)" : "var(--border, #e5e5e5)"}`,
                          background: section.columns === n ? "var(--accent, #268599)" : "#fff",
                          color: section.columns === n ? "#fff" : "var(--fg-2, #404040)",
                          fontSize: 12, fontWeight: 600, cursor: "pointer",
                        }}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Reset */}
              <div style={{ height: 1, background: "var(--border, #e5e5e5)", margin: "4px 6px" }} />
              <MenuRow
                icon={<Broom size={16} />}
                label={t`Reset`}
                disabled={!hasItems}
                onClick={() => {
                  onResetItems();
                  closeMobileSheet();
                }}
              />

              {/* Delete / Hide Section */}
              <div style={{ height: 1, background: "var(--border, #e5e5e5)", margin: "4px 6px" }} />
              <MenuRow
                icon={actionIcon}
                label={actionText}
                muted
                onClick={() => {
                  closeMobileSheet();
                  setTimeout(onRemove, 300);
                }}
              />
            </div>
          </SheetContent>
        </Sheet>

        <IconPickerModal
          open={iconModalOpen}
          currentIcon={(section as any).icon || ""}
          onSelect={(kebab) => setValue(`sections.${id}.icon`, kebab)}
          onClose={() => setIconModalOpen(false)}
        />
      </>
    );
  }

  // Desktop — custom dropdown matching sec-menu-pop design spec
  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      {/* Trigger button */}
      <button
        onClick={() => {
          setDesktopOpen((v) => !v);
          setSubOpen(null);
        }}
        aria-label={t`Section options`}
        aria-haspopup="true"
        aria-expanded={desktopOpen}
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          border: "1px solid var(--border, #e5e5e5)",
          background: "#fff",
          color: "var(--fg-2, #404040)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          flexShrink: 0,
          transition: "color 0.15s, border-color 0.15s",
        }}
        onMouseEnter={(e) => {
          const el = e.currentTarget as HTMLButtonElement;
          el.style.color = "var(--accent, #268599)";
          el.style.borderColor = "var(--accent, #268599)";
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget as HTMLButtonElement;
          el.style.color = "var(--fg-2, #404040)";
          el.style.borderColor = "var(--border, #e5e5e5)";
        }}
      >
        <DotsThree size={18} weight="bold" />
      </button>

      {/* Dropdown panel */}
      {desktopOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 1000,
            background: "#fff",
            color: "var(--fg-1, #171717)",
            borderRadius: 10,
            boxShadow: "0 8px 28px rgba(22,52,76,0.12)",
            border: "1px solid rgba(0,0,0,0.06)",
            padding: 4,
            width: 220,
          }}
        >
          {/* Group 1: Add + Separate Links */}
          {hasItems && (
            <>
              <MenuRow
                icon={<Plus size={16} />}
                label={t`Add a new item`}
                shortcut="⌘N"
                primaryIcon
                onClick={() => {
                  onCreate();
                  closeDropdown();
                }}
              />
              <MenuRow
                icon={<LinkSimple size={16} />}
                label={t`Separate Links`}
                checked={Boolean(section.separateLinks)}
                onCheckedChange={(v) => { toggleSeperateLinks(v); }}
              />
              <div style={{ height: 1, background: "var(--border, #e5e5e5)", margin: "4px 6px" }} />
            </>
          )}

          {/* Hide / Show */}
          <MenuRow
            icon={section.visible ? <Eye size={16} /> : <EyeSlash size={16} />}
            label={section.visible ? t`Hide` : t`Show`}
            shortcut="⌘H"
            onClick={() => {
              toggleVisibility();
              closeDropdown();
            }}
          />

          {/* Rename (accordion) */}
          <MenuRow
            icon={<PencilSimple size={16} />}
            label={t`Rename`}
            hasSub
            subOpen={subOpen === "rename"}
            onClick={() => setSubOpen(subOpen === "rename" ? null : "rename")}
          />
          {subOpen === "rename" && (
            <div style={{ padding: "4px 10px 8px" }}>
              <div style={{ display: "flex", gap: 4 }}>
                <input
                  autoFocus
                  value={section.name}
                  onChange={(e) => setValue(`sections.${id}.name`, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "Escape") closeDropdown();
                  }}
                  style={{
                    flex: 1,
                    height: 32,
                    border: "1px solid var(--border, #e5e5e5)",
                    borderRadius: 6,
                    padding: "0 8px",
                    fontSize: 13,
                    color: "var(--fg-1, #171717)",
                    background: "#fff",
                    outline: "none",
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = "var(--accent, #268599)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = "var(--border, #e5e5e5)";
                  }}
                />
                <button
                  onClick={onResetName}
                  title={t`Reset name`}
                  style={{
                    width: 32,
                    height: 32,
                    border: "1px solid var(--border, #e5e5e5)",
                    borderRadius: 6,
                    background: "#fff",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--fg-3, #737373)",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = "#f5f5f5";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = "#fff";
                  }}
                >
                  <ArrowCounterClockwise size={14} />
                </button>
              </div>
            </div>
          )}

          {/* Change Icon — opens visual picker modal */}
          <MenuRow
            icon={<Palette size={16} />}
            label={t`Change Icon`}
            onClick={() => {
              closeDropdown();
              setTimeout(() => setIconModalOpen(true), 50);
            }}
          />

          {/* Columns (accordion) */}
          <MenuRow
            icon={<Columns size={16} />}
            label={t`Columns`}
            hasSub
            subOpen={subOpen === "columns"}
            onClick={() => setSubOpen(subOpen === "columns" ? null : "columns")}
          />
          {subOpen === "columns" && (
            <div style={{ padding: "4px 10px 8px" }}>
              <div style={{ display: "flex", gap: 4 }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    onClick={() => {
                      onChangeColumns(String(n));
                      closeDropdown();
                    }}
                    style={{
                      flex: 1,
                      height: 28,
                      borderRadius: 6,
                      border: `1px solid ${section.columns === n ? "var(--accent, #268599)" : "var(--border, #e5e5e5)"}`,
                      background: section.columns === n ? "var(--accent, #268599)" : "#fff",
                      color: section.columns === n ? "#fff" : "var(--fg-2, #404040)",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      if (section.columns !== n)
                        (e.currentTarget as HTMLButtonElement).style.background = "#f5f5f5";
                    }}
                    onMouseLeave={(e) => {
                      if (section.columns !== n)
                        (e.currentTarget as HTMLButtonElement).style.background = "#fff";
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Reset */}
          <div style={{ height: 1, background: "var(--border, #e5e5e5)", margin: "4px 6px" }} />
          <MenuRow
            icon={<Broom size={16} />}
            label={t`Reset`}
            disabled={!hasItems}
            onClick={() => {
              onResetItems();
              closeDropdown();
            }}
          />

          {/* Delete / Hide Section */}
          <div style={{ height: 1, background: "var(--border, #e5e5e5)", margin: "4px 6px" }} />
          <MenuRow
            icon={actionIcon}
            label={actionText}
            muted
            onClick={() => {
              closeDropdown();
              setTimeout(onRemove, 100);
            }}
          />
        </div>
      )}

      {/* Icon picker modal — rendered outside the dropdown so z-index works */}
      <IconPickerModal
        open={iconModalOpen}
        currentIcon={(section as any).icon || ""}
        onSelect={(kebab) => setValue(`sections.${id}.icon`, kebab)}
        onClose={() => setIconModalOpen(false)}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Small helper — a single menu row rendered with inline styles so it always
// renders white/light regardless of any parent dark-mode context.
// ---------------------------------------------------------------------------
type MenuRowProps = {
  icon: React.ReactNode;
  label: string;
  shortcut?: string;
  primaryIcon?: boolean;
  muted?: boolean;
  hasSub?: boolean;
  subOpen?: boolean;
  disabled?: boolean;
  checked?: boolean;
  onCheckedChange?: (v: boolean) => void;
  onClick?: () => void;
};

const MenuRow = ({
  icon,
  label,
  shortcut,
  primaryIcon,
  muted,
  hasSub,
  subOpen,
  disabled,
  checked,
  onCheckedChange,
  onClick,
}: MenuRowProps) => {
  const [hovered, setHovered] = useState(false);

  const isCheckRow = onCheckedChange !== undefined;

  const bg = subOpen || (hovered && !disabled) ? "#f5f5f5" : "transparent";
  const textColor =
    muted && hovered ? "var(--scv-danger, #dc2626)" : muted ? "var(--fg-3, #737373)" : "var(--fg-1, #171717)";
  const iconColor = primaryIcon
    ? "var(--scv-coral-deep, #e8885e)"
    : muted && hovered
      ? "var(--scv-danger, #dc2626)"
      : "var(--fg-3, #737373)";

  const handleClick = () => {
    if (isCheckRow && onCheckedChange) {
      onCheckedChange(!checked);
    } else {
      onClick?.();
    }
  };

  return (
    <button
      disabled={disabled}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        padding: "7px 10px",
        fontSize: 13,
        fontWeight: 500,
        borderRadius: 6,
        border: "none",
        background: bg,
        color: textColor,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        transition: "background 0.1s, color 0.1s",
        textAlign: "left",
      }}
    >
      {/* Icon tile */}
      <span
        style={{
          width: 16,
          height: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: iconColor,
          flexShrink: 0,
          transition: "color 0.1s",
        }}
      >
        {icon}
      </span>

      {/* Label */}
      <span style={{ flex: 1 }}>{label}</span>

      {/* Keyboard shortcut */}
      {shortcut && (
        <span
          style={{
            fontSize: 10,
            color: "var(--fg-4, #a3a3a3)",
            background: "transparent",
            flexShrink: 0,
          }}
        >
          {shortcut}
        </span>
      )}

      {/* Sub-menu caret */}
      {hasSub && (
        <svg
          width={11}
          height={11}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          style={{ opacity: 0.4, flexShrink: 0 }}
        >
          <path d={subOpen ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} />
        </svg>
      )}

      {/* Checkbox indicator */}
      {isCheckRow && (
        <span
          style={{
            width: 16,
            height: 16,
            borderRadius: 4,
            border: `2px solid ${checked ? "var(--accent, #268599)" : "var(--border, #e5e5e5)"}`,
            background: checked ? "var(--accent, #268599)" : "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "border-color 0.1s, background 0.1s",
          }}
        >
          {checked && (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
              <path d="M5 13l4 4L19 7" />
            </svg>
          )}
        </span>
      )}
    </button>
  );
};
